<template>
    <div class="sp-paso">
        <header class="sp-paso__head">
            <span class="sp-icon-badge"><i class="pi pi-file" aria-hidden="true"></i></span>
            <div>
                <h2 class="sp-h3">Información de facturación</h2>
                <p>Utilizaremos estos datos para gestionar la facturación electrónica de los servicios contratados.</p>
            </div>
        </header>

        <p class="sp-aviso-dian">
            <i class="pi pi-info-circle" aria-hidden="true"></i>
            <span>
                Según la DIAN, se requiere nombre o razón social, tipo y número de identificación
                y correo electrónico para expedir la factura.
            </span>
        </p>

        <fieldset class="sp-mismos">
            <legend>¿Los datos de facturación son los mismos del negocio?</legend>
            <div class="sp-mismos__opciones">
                <label class="sp-mismos__opcion" :class="{ 'is-activa': datos.usa_datos_negocio === true }">
                    <RadioButton v-model="datos.usa_datos_negocio" inputId="sp-fact-si" name="usa_datos" :value="true" />
                    <span>Sí, usar los mismos datos del negocio</span>
                </label>
                <label class="sp-mismos__opcion" :class="{ 'is-activa': datos.usa_datos_negocio === false }">
                    <RadioButton v-model="datos.usa_datos_negocio" inputId="sp-fact-no" name="usa_datos" :value="false" />
                    <span>No, usar datos diferentes</span>
                </label>
            </div>
        </fieldset>

        <!-- Resumen editable de los datos heredados del negocio -->
        <div v-if="datos.usa_datos_negocio" class="sp-heredado">
            <h3>Se facturará a nombre de</h3>
            <dl>
                <div><dt>Razón social</dt><dd>{{ negocio.razon_social || '—' }}</dd></div>
                <div><dt>Identificación</dt><dd>{{ identificacionNegocio }}</dd></div>
                <div><dt>Dirección</dt><dd>{{ negocio.direccion || '—' }}</dd></div>
            </dl>
            <button type="button" class="sp-enlace" @click="datos.usa_datos_negocio = false">
                <i class="pi pi-pencil" aria-hidden="true"></i>
                <span>Usar datos diferentes</span>
            </button>
        </div>

        <!-- Datos fiscales propios -->
        <template v-else>
            <fieldset class="sp-tipo-persona">
                <legend>Tipo de persona <span class="sp-campo__req" aria-hidden="true">*</span></legend>
                <div class="sp-mismos__opciones">
                    <label class="sp-mismos__opcion" :class="{ 'is-activa': datos.tipo_persona === 'JURIDICA' }">
                        <RadioButton v-model="datos.tipo_persona" inputId="sp-ftp-j" name="fact_tp" value="JURIDICA" />
                        <span>Persona jurídica</span>
                    </label>
                    <label class="sp-mismos__opcion" :class="{ 'is-activa': datos.tipo_persona === 'NATURAL' }">
                        <RadioButton v-model="datos.tipo_persona" inputId="sp-ftp-n" name="fact_tp" value="NATURAL" />
                        <span>Persona natural</span>
                    </label>
                </div>
            </fieldset>

            <div class="sp-campo">
                <label :for="id('razon')">
                    <span>Razón social o nombre</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <InputText :id="id('razon')" v-model.trim="datos.razon_social" :class="{ 'p-invalid': error('razon_social') }" />
                <p v-if="error('razon_social')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('razon_social') }}</span>
                </p>
            </div>

            <div class="sp-form__row sp-form__row--nit">
                <div class="sp-campo">
                    <label :for="id('tipodoc')"><span>Tipo de identificación</span></label>
                    <Dropdown
                        :id="id('tipodoc')"
                        v-model="datos.id_tipo_identificacion"
                        :options="tiposIdentificacion"
                        optionLabel="descripcion"
                        optionValue="id"
                        placeholder="Selecciona el tipo"
                    />
                </div>

                <div class="sp-campo">
                    <label :for="id('dv')"><span>Dígito de verificación</span></label>
                    <InputText :id="id('dv')" v-model.trim="datos.digito_verificacion" inputmode="numeric" maxlength="2" />
                </div>
            </div>

            <div class="sp-campo">
                <label :for="id('num')">
                    <span>Número de identificación</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <InputText
                    :id="id('num')"
                    v-model.trim="datos.numero_identificacion"
                    inputmode="numeric"
                    :class="{ 'p-invalid': error('numero_identificacion') }"
                />
                <p v-if="error('numero_identificacion')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('numero_identificacion') }}</span>
                </p>
            </div>

            <div class="sp-campo">
                <label :for="id('direccion')">
                    <span>Dirección de facturación</span><span class="sp-campo__req" aria-hidden="true">*</span>
                </label>
                <InputText :id="id('direccion')" v-model.trim="datos.direccion" :class="{ 'p-invalid': error('direccion') }" />
                <p v-if="error('direccion')" class="sp-campo__error">
                    <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('direccion') }}</span>
                </p>
            </div>

            <div class="sp-form__row">
                <div class="sp-campo">
                    <label :for="id('depto')">
                        <span>Departamento</span><span class="sp-campo__req" aria-hidden="true">*</span>
                    </label>
                    <Dropdown
                        :id="id('depto')"
                        v-model="datos.departamento"
                        :options="departamentos"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Selecciona el departamento"
                        :filter="true"
                        :class="{ 'p-invalid': error('departamento') }"
                        @change="datos.id_divipola = null"
                    />
                </div>

                <div class="sp-campo">
                    <label :for="id('municipio')">
                        <span>Municipio</span><span class="sp-campo__req" aria-hidden="true">*</span>
                    </label>
                    <Dropdown
                        :id="id('municipio')"
                        v-model="datos.id_divipola"
                        :options="municipios"
                        optionLabel="label"
                        optionValue="value"
                        :disabled="!datos.departamento"
                        :filter="true"
                        placeholder="Selecciona el municipio"
                        :class="{ 'p-invalid': error('id_divipola') }"
                    />
                </div>
            </div>
        </template>

        <div class="sp-campo">
            <label :for="id('email')">
                <span>Correo para recibir la factura</span><span class="sp-campo__req" aria-hidden="true">*</span>
            </label>
            <InputText
                :id="id('email')"
                v-model.trim="datos.email_facturacion"
                type="email"
                placeholder="facturacion@negocio.com"
                :class="{ 'p-invalid': error('email_facturacion') }"
            />
            <p v-if="error('email_facturacion')" class="sp-campo__error">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ error('email_facturacion') }}</span>
            </p>
            <p v-else class="sp-campo__ayuda">Puede ser distinto al correo de tu cuenta.</p>
        </div>

        <!-- RUT: opcional en el registro -->
        <div class="sp-rut">
            <h3>Información tributaria <span>(opcional)</span></h3>
            <p>Puedes adjuntar el RUT ahora o completarlo después desde tu perfil.</p>

            <label class="sp-rut__caja" :class="{ 'is-cargado': !!rutNombre }">
                <input type="file" :accept="acepta" class="sp-rut__input" @change="alSeleccionar" />
                <i :class="rutNombre ? 'pi pi-check-circle' : 'pi pi-cloud-upload'" aria-hidden="true"></i>
                <span class="sp-rut__titulo">{{ rutNombre || 'Adjuntar RUT' }}</span>
                <span class="sp-rut__ayuda">PDF o imagen · Máx. {{ maxMb }} MB</span>
            </label>

            <button v-if="rutNombre" type="button" class="sp-enlace" @click="quitarRut">
                <i class="pi pi-times" aria-hidden="true"></i><span>Quitar archivo</span>
            </button>

            <p v-if="errorRut" class="sp-campo__error">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i><span>{{ errorRut }}</span>
            </p>
        </div>

        <!-- Términos y privacidad -->
        <div class="sp-legal">
            <div class="sp-campo sp-campo--check" :class="{ 'sp-campo--error': error('acepto_terminos') }">
                <Checkbox v-model="legal.acepto_terminos" :binary="true" inputId="sp-terminos" />
                <label for="sp-terminos">
                    Acepto los
                    <router-link to="/terminos-condiciones" target="_blank">Términos y Condiciones</router-link>
                    de Seway Plus.
                </label>
            </div>

            <div class="sp-campo sp-campo--check" :class="{ 'sp-campo--error': error('acepto_privacidad') }">
                <Checkbox v-model="legal.acepto_privacidad" :binary="true" inputId="sp-privacidad" />
                <label for="sp-privacidad">
                    He leído y acepto la
                    <router-link to="/politica-privacidad" target="_blank">Política de Privacidad</router-link>.
                </label>
            </div>

            <p v-if="error('acepto_terminos') || error('acepto_privacidad')" class="sp-campo__error">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                <span>Debes aceptar ambos documentos para crear la cuenta.</span>
            </p>
        </div>
    </div>
</template>

<script>
import { departamentosDe, municipiosDe } from '../catalogos';
import { RUT_ACEPTA, RUT_MAX_MB } from '../config';

export default {
    name: 'PasoFacturacion',
    emits: ['rut'],
    props: {
        datos: { type: Object, required: true },
        legal: { type: Object, required: true },
        negocio: { type: Object, required: true },
        errores: { type: Object, default: () => ({}) },
        tiposIdentificacion: { type: Array, default: () => [] },
        divipola: { type: Array, default: () => [] },
        rutNombre: { type: String, default: '' }
    },
    data() {
        return {
            errorRut: '',
            acepta: RUT_ACEPTA,
            maxMb: RUT_MAX_MB
        };
    },
    computed: {
        departamentos() {
            return departamentosDe(this.divipola);
        },
        municipios() {
            return municipiosDe(this.divipola, this.datos.departamento);
        },
        identificacionNegocio() {
            if (!this.negocio.nit) return '—';
            return this.negocio.digito_verificacion
                ? this.negocio.nit + '-' + this.negocio.digito_verificacion
                : this.negocio.nit;
        }
    },
    methods: {
        id(campo) {
            return 'sp-fact-' + campo;
        },
        error(campo) {
            return this.errores[campo] || '';
        },
        alSeleccionar(evento) {
            this.errorRut = '';
            const archivo = evento.target.files && evento.target.files[0];

            if (!archivo) {
                this.$emit('rut', null);
                return;
            }

            const extension = (archivo.name.split('.').pop() || '').toLowerCase();
            if (['pdf', 'jpg', 'jpeg', 'png'].indexOf(extension) === -1) {
                this.errorRut = 'El RUT debe ser un archivo PDF, JPG o PNG.';
                evento.target.value = '';
                this.$emit('rut', null);
                return;
            }

            if (archivo.size > RUT_MAX_MB * 1024 * 1024) {
                this.errorRut = 'El archivo no puede superar ' + RUT_MAX_MB + ' MB.';
                evento.target.value = '';
                this.$emit('rut', null);
                return;
            }

            this.$emit('rut', archivo);
        },
        quitarRut() {
            this.errorRut = '';
            this.$emit('rut', null);
        }
    }
};
</script>

<style lang="scss" scoped>
.sp-paso {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.sp-paso__head {
    display: flex;
    align-items: center;
    gap: 1rem;

    p {
        margin-top: 0.25rem;
        color: var(--sp-text-soft);
        font-size: 0.92rem;
    }
}

.sp-aviso-dian {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    padding: 0.9rem 1.1rem;
    background: rgba(47, 128, 237, 0.07);
    border-left: 4px solid #2f80ed;
    border-radius: 0 10px 10px 0;
    color: var(--sp-navy);
    font-size: 0.85rem;

    i {
        flex: 0 0 auto;
        margin-top: 0.15rem;
        color: #2f80ed;
    }
}

.sp-mismos,
.sp-tipo-persona {
    margin: 0;
    padding: 0;
    border: 0;

    legend {
        padding: 0;
        margin-bottom: 0.6rem;
        color: var(--sp-navy);
        font-size: 0.9rem;
        font-weight: 700;
    }
}

.sp-mismos__opciones {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
}

.sp-mismos__opcion {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.85rem 1rem;
    background: var(--sp-surface);
    border: 1.5px solid var(--sp-border);
    border-radius: 12px;
    cursor: pointer;
    color: var(--sp-navy);
    font-size: 0.92rem;
    font-weight: 600;
    transition: border-color 0.2s ease, background 0.2s ease;

    &.is-activa {
        border-color: var(--sp-orange);
        background: var(--sp-orange-50);
    }
}

.sp-heredado {
    padding: 1.1rem 1.25rem;
    background: var(--sp-surface-alt);
    border: 1px solid var(--sp-border);
    border-radius: 14px;

    h3 {
        margin-bottom: 0.75rem;
        font-size: 0.9rem;
        font-weight: 700;
    }

    dl {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin: 0 0 0.75rem 0;
    }

    div {
        display: flex;
        flex-wrap: wrap;
        gap: 0.35rem;
    }

    dt {
        color: var(--sp-text-soft);
        font-size: 0.85rem;

        &::after {
            content: ':';
        }
    }

    dd {
        margin: 0;
        color: var(--sp-navy);
        font-size: 0.85rem;
        font-weight: 600;
        overflow-wrap: anywhere;
    }
}

.sp-enlace {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0;
    background: none;
    border: 0;
    color: var(--sp-orange);
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
}

.sp-rut {
    padding: 1.1rem 1.25rem;
    background: var(--sp-surface-alt);
    border: 1px solid var(--sp-border);
    border-radius: 14px;

    h3 {
        font-size: 0.95rem;
        font-weight: 700;

        span {
            color: var(--sp-text-soft);
            font-weight: 500;
        }
    }

    > p {
        margin: 0.35rem 0 0.9rem;
        color: var(--sp-text-soft);
        font-size: 0.85rem;
    }
}

.sp-rut__caja {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.3rem;
    padding: 1.4rem 1rem;
    background: var(--sp-surface);
    border: 2px dashed var(--sp-border);
    border-radius: 12px;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.2s ease, background 0.2s ease;

    &:hover,
    &:focus-within {
        border-color: var(--sp-orange);
        background: var(--sp-orange-50);
    }

    &.is-cargado {
        border-style: solid;
        border-color: var(--sp-orange);
        background: var(--sp-orange-50);
    }

    i {
        color: var(--sp-orange);
        font-size: 1.6rem;
    }
}

.sp-rut__input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
}

.sp-rut__titulo {
    color: var(--sp-navy);
    font-size: 0.92rem;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.sp-rut__ayuda {
    color: var(--sp-text-soft);
    font-size: 0.78rem;
}

.sp-legal {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.25rem;
}

.sp-campo__ayuda {
    color: var(--sp-text-soft);
    font-size: 0.78rem;
}

@media (min-width: 576px) {
    .sp-mismos__opciones {
        grid-template-columns: 1fr 1fr;
    }

    .sp-form__row--nit {
        grid-template-columns: 2fr 1fr;
    }
}
</style>
